import React from 'react';
import { 
    Box,
    Grid,
    styled,
    Typography,
    Container,
    Card,
    CardContent,
    Chip,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const PricingPlans = () => {
    const Section = styled(Box)(({ theme }) => ({
        padding: theme.spacing(10, 0),
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    }));

    const SectionTitle = styled(Typography)(({ theme }) => ({
        fontFamily: "'Outfit', 'Roboto', sans-serif",
        fontWeight: 800,
        fontSize: 'clamp(2rem, 4vw, 3rem)',
        textAlign: 'center',
        marginBottom: theme.spacing(2),
        background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
    }));

    const SectionSubtitle = styled(Typography)(({ theme }) => ({
        textAlign: 'center',
        color: '#64748b',
        fontSize: '1.1rem',
        marginBottom: theme.spacing(8),
        maxWidth: '800px',
        marginLeft: 'auto',
        marginRight: 'auto',
        lineHeight: 1.8,
    }));

    const PricingCard = styled(Card)(({ theme, featured }) => ({
        height: '100%',
        borderRadius: '24px',
        border: featured ? '3px solid #FF6B35' : '1px solid rgba(0, 0, 0, 0.08)',
        boxShadow: featured 
            ? '0 20px 60px rgba(255, 107, 53, 0.25)' 
            : '0 10px 40px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'visible',
        transform: featured ? 'scale(1.05)' : 'scale(1)',
        background: featured 
            ? 'linear-gradient(135deg, #fff 0%, #fff5f0 100%)' 
            : '#fff',
        '&:hover': {
            transform: featured ? 'scale(1.08)' : 'scale(1.03)',
            boxShadow: '0 25px 70px rgba(255, 107, 53, 0.2)',
        },
        [theme.breakpoints.down('md')]: {
            transform: 'scale(1)',
            '&:hover': {
                transform: 'scale(1.02)',
            },
        },
    }));

    const PopularBadge = styled(Chip)(({ theme }) => ({
        position: 'absolute',
        top: '-12px',
        right: '20px',
        background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)',
        color: '#fff',
        fontWeight: 700,
        fontSize: '0.85rem',
        padding: '4px 8px',
        height: 'auto',
        boxShadow: '0 4px 15px rgba(255, 107, 53, 0.4)',
    }));

    const IconWrapper = styled(Box)(({ theme }) => ({
        width: '80px',
        height: '80px',
        borderRadius: '20px',
        background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto',
        marginBottom: theme.spacing(3),
        boxShadow: '0 10px 30px rgba(255, 107, 53, 0.3)',
        '& svg': {
            fontSize: '2.5rem',
            color: '#fff',
        },
    }));

    const PlanTitle = styled(Typography)(({ theme }) => ({
        fontFamily: "'Outfit', 'Roboto', sans-serif",
        fontWeight: 700,
        fontSize: '1.75rem',
        color: '#1e293b',
        marginBottom: theme.spacing(1),
        textAlign: 'center',
    }));

    const PlanDescription = styled(Typography)(({ theme }) => ({
        fontSize: '1rem',
        color: '#64748b',
        marginBottom: theme.spacing(3),
        textAlign: 'center',
        lineHeight: 1.6,
    }));

    const PriceBox = styled(Box)(({ theme }) => ({
        textAlign: 'center',
        padding: theme.spacing(3, 0),
        marginBottom: theme.spacing(3),
        borderTop: '1px solid rgba(0, 0, 0, 0.08)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
    }));

    const PriceText = styled(Typography)(({ theme }) => ({
        fontFamily: "'Outfit', 'Roboto', sans-serif",
        fontWeight: 800,
        fontSize: '2.5rem',
        color: '#FF6B35',
        lineHeight: 1,
    }));

    const PriceSubtext = styled(Typography)({
        fontSize: '0.95rem',
        color: '#64748b',
        marginTop: '8px',
    });

    const FeatureList = styled(Box)(({ theme }) => ({
        marginTop: theme.spacing(3),
    }));

    const FeatureItem = styled(Box)(({ theme }) => ({
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(2),
        '& svg': {
            color: '#FF6B35',
            fontSize: '1.5rem',
            marginTop: '2px',
        },
        '& p': {
            fontSize: '0.95rem',
            color: '#475569',
            lineHeight: 1.6,
        },
    }));

    const plans = [
        {
            icon: <AccessTimeIcon />,
            title: 'Hourly Rental',
            description: 'Perfect for short-term projects and quick lifting operations',
            price: 'Custom',
            priceSubtext: 'Based on crane capacity & hours',
            features: [
                'Minimum 1 hours booking',
                'Flexible scheduling',
                'Expert crane operators included',
                'All safety equipment provided',
                'Ideal for small construction tasks',
                'Pay only for hours used',
            ],
            featured: false,
        },
        {
            icon: <CalendarMonthIcon />,
            title: 'Monthly Rental',
            description: 'Best value for long-term projects and ongoing construction work',
            price: 'Best Rates',
            priceSubtext: 'Competitive monthly packages',
            features: [
                'Significant cost savings',
                'Dedicated crane for your project',
                'Priority support & maintenance',
                'Flexible contract terms',
                'Free operator training',
                'Regular equipment inspection',
                '24/7 emergency support',
            ],
            featured: true,
        },
        {
            icon: <LocalOfferIcon />,
            title: 'Project-Based',
            description: 'Customized solutions for specific project requirements',
            price: 'Negotiable',
            priceSubtext: 'Tailored to your needs',
            features: [
                'Custom pricing based on scope',
                'Multiple crane options',
                'Site assessment included',
                'Lift planning assistance',
                'Dedicated project manager',
                'Insurance coverage options',
            ],
            featured: false,
        },
    ];

    return (
        <Section component="section">
            <Container maxWidth="lg">
                <SectionTitle variant="h2">
                    Flexible Crane Rental Plans
                </SectionTitle>
                <SectionSubtitle>
                    Choose the perfect rental plan for your project in Coimbatore. We offer hourly, 
                    monthly, and project-based crane rental options with competitive rates and 
                    professional service.
                </SectionSubtitle>

                <Grid container spacing={4} alignItems="stretch">
                    {plans.map((plan, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <PricingCard featured={plan.featured ? 1 : 0}>
                                {plan.featured && (
                                    <PopularBadge label="Most Popular" />
                                )}
                                <CardContent sx={{ p: 4 }}>
                                    <IconWrapper>
                                        {plan.icon}
                                    </IconWrapper>
                                    
                                    <PlanTitle>
                                        {plan.title}
                                    </PlanTitle>
                                    
                                    <PlanDescription>
                                        {plan.description}
                                    </PlanDescription>

                                    <PriceBox>
                                        <PriceText>
                                            {plan.price}
                                        </PriceText>
                                        <PriceSubtext>
                                            {plan.priceSubtext}
                                        </PriceSubtext>
                                    </PriceBox>

                                    <FeatureList>
                                        {plan.features.map((feature, idx) => (
                                            <FeatureItem key={idx}>
                                                <CheckCircleIcon />
                                                <Typography>{feature}</Typography>
                                            </FeatureItem>
                                        ))}
                                    </FeatureList>
                                </CardContent>
                            </PricingCard>
                        </Grid>
                    ))}
                </Grid>

                <Box sx={{ textAlign: 'center', mt: 6 }}>
                    <Typography 
                        variant="body1" 
                        sx={{ 
                            color: '#64748b',
                            fontSize: '1rem',
                            fontStyle: 'italic',
                        }}
                    >
                        All plans include professional operators, safety equipment, and insurance. 
                        Contact us for detailed pricing and custom packages.
                    </Typography>
                </Box>
            </Container>
        </Section>
    );
};

export default PricingPlans;
